import { useQuery } from '@apollo/client'
import { Search } from '@mui/icons-material'
import {
    Box,
    Button,
    Card,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    Stack,
    Switch,
    TextField,
} from '@mui/material'
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { AdminGetRecruiters, RecruiterMe } from 'recruiter/graphql/admin.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getPxAsRem } from 'shared/utils'
import { AdminHeader } from './AdminHeader'
import RecruiterModal, { type RecruiterToEdit } from './RecruiterModal'
import RecruitersTable from './RecruitersTable'

export default function AdminDashboard() {
    const toast = useToast()
    const [modalOpen, setModalOpen] = useState(false)
    const [editingRecruiter, setEditingRecruiter] =
        useState<RecruiterToEdit | null>(null)
    const [searchValue, setSearchValue] = useState('')
    const [showInactive, setShowInactive] = useState(false)

    usePageTitle('Admin · Manage Recruiters')

    const { data: meData, loading: meLoading } = useQuery(RecruiterMe)
    const me = meData?.recruiterMe

    const { data, refetch } = useQuery(AdminGetRecruiters, {
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: error.message,
            })
        },
    })

    // Role gate: bot-admin-only users who land on /admin should bounce
    // to the Bot Audit sub-tab since they can't use Recruiters. Show a
    // spinner rather than the Recruiters table while `me` is loading so
    // the redirect window doesn't flash the wrong page.
    if (meLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        )
    }
    if (me && !me.isAdmin && me.isBotAdmin) {
        return <Navigate to={recruiterRouteConfig.botAudit.path} replace />
    }

    const handleAdd = () => {
        setEditingRecruiter(null)
        setModalOpen(true)
    }

    const handleEdit = (recruiter: RecruiterToEdit) => {
        setEditingRecruiter(recruiter)
        setModalOpen(true)
    }

    const handleModalClose = () => {
        setModalOpen(false)
        setEditingRecruiter(null)
    }

    return (
        <>
            <Stack
                direction="column"
                spacing={2}
                sx={{
                    padding: getPxAsRem(16, 24),
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <AdminHeader
                    isAdmin={me?.isAdmin ?? false}
                    isBotAdmin={me?.isBotAdmin ?? false}
                />
                <Card
                    sx={{
                        padding: getPxAsRem(16),
                        flex: 1,
                        overflow: 'hidden',
                        borderRadius: getPxAsRem(8),
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{ flex: 1, overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexShrink: 0,
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >
                                <TextField
                                    onChange={(e) =>
                                        setSearchValue(e?.target?.value ?? '')
                                    }
                                    variant="standard"
                                    value={searchValue}
                                    placeholder="Search Name"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showInactive}
                                            onChange={(_, checked) =>
                                                setShowInactive(checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label="Show Inactive"
                                />
                            </Stack>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={handleAdd}
                            >
                                Add Recruiter
                            </Button>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            <RecruitersTable
                                data={data}
                                searchInput={searchValue}
                                showInactive={showInactive}
                                refetch={refetch}
                                onEdit={handleEdit}
                            />
                        </Box>
                    </Stack>
                </Card>
            </Stack>
            <RecruiterModal
                open={modalOpen}
                recruiter={editingRecruiter}
                onCancel={handleModalClose}
                refetch={refetch}
            />
        </>
    )
}
